import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Upload, Trash2, Loader2, Search } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { AdminTitle, Btn, Field, inputCls } from "@/pages/admin/Admin";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { ImageGallery, VariantEditor, ColorLibrary, useColors } from "@/pages/admin/ProductParts";
import NumInput from "@/components/NumInput";
import { useSort, SortTh } from "@/components/SortTh";

const PRODUCT_SORT = { name: (p) => p.name.toLowerCase(), type: (p) => p.type, price: (p) => p.price, stock: (p) => (p.type === "physical" ? Number(p.stock) || 0 : 1e12), status: (p) => (p.active ? 0 : 1) };
const PRODUCT_COLS = [["", null], ["Product", "name"], ["Type", "type"], ["Price", "price"], ["Stock", "stock"], ["Status", "status"], ["", null]];

export function Uploader({ onDone, visibility = "public", accept, label = "Upload", testid }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("visibility", visibility);
      const r = await api.post("/admin/upload", fd);
      onDone(r.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <label className="inline-flex items-center gap-2 border border-[#3B220C] px-3 py-2 text-xs cursor-pointer hover:bg-[#F6F2EE]" data-testid={testid}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}{label}
      <input type="file" className="hidden" accept={accept} onChange={upload} />
    </label>
  );
}

const toLocal = (iso) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "");

const BLANK = { type: "physical", name: "", name_id: "", slug: "", description: "", description_id: "", price: 0, images: [], category: "essential", stock: 0, weight_g: 1000,
  out_of_stock_mode: "show", preorder_days: 14, prep_days: 2,
  variants: [], files: [], download_limit: 3, lessons: [], mode: null, sessions: [], venue: "", addon_product_id: null, featured: false, active: true };

function RowList({ rows, onChange, cols, add, testid }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap gap-2 items-center border border-[#E6DFD5] p-2 bg-[#FAFAFA]">
          {cols.map(([k, ph, type, w]) => type === "upload"
            ? <Uploader key={k} visibility={w} label={r[k] ? "Replace file" : "Upload file"} onDone={(d) => set(i, k, d.path)} testid={`${testid}-upload-${i}`} />
            : type === "number" ? <NumInput key={k} placeholder={ph} value={r[k] ?? 0} onChange={(v) => set(i, k, v)} className={`${inputCls} ${w ? `!${w}` : "flex-1 min-w-[120px]"}`} data-testid={`${testid}-${k}-${i}`} />
            : <input key={k} type={type || "text"} placeholder={ph} value={type === "datetime-local" ? toLocal(r[k]) : r[k] ?? ""} className={`${inputCls} ${w ? `!${w}` : "flex-1 min-w-[120px]"}`} data-testid={`${testid}-${k}-${i}`}
              onChange={(e) => { if (type === "datetime-local" && !e.target.value) return; set(i, k, type === "datetime-local" ? new Date(e.target.value).toISOString() : e.target.value); }} />)}
          {r.storage_path && <span className="text-[10px] text-[#2E6B40]">file ✓</span>}
          <button onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`${testid}-remove-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button onClick={() => onChange([...rows, add()])} className="text-xs underline" data-testid={`${testid}-add`}>+ Add row</button>
    </div>
  );
}

function ProductForm({ initial, all, onSaved, onClose }) {
  const [f, setF] = useState({ out_of_stock_mode: "show", preorder_days: 14, prep_days: 2, ...initial, weight_g: initial.weight_g || 1000 });
  const [colors] = useColors();
  const [cols, setCols] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/collections").then((r) => setCols(r.data)); }, []);
  const s = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setF((prev) => ({ ...prev, [k]: v, ...(k === "type" && v === "digital" && !prev.files.length ? { files: [{ name: "", format: "PDF", delta: 0 }] } : {}) }));
  };
  const n = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, variants: f.variants.filter((v) => v.name.trim()).map((v) => ({ ...v, options: v.options.filter((o) => o.label.trim()) })), mode: f.type === "course" ? "self" : f.type === "workshop" ? f.mode || "inperson" : null };
      const r = f.product_id ? await api.put(`/admin/products/${f.product_id}`, body) : await api.post("/admin/products", body);
      toast.success("Saved");
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 text-sm" data-testid="product-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Type"><select value={f.type} onChange={s("type")} className={inputCls} data-testid="pf-type">{["physical", "digital", "course", "workshop"].map((t) => <option key={t}>{t}</option>)}</select></Field>
        <Field label="Collection"><select value={f.category} onChange={s("category")} className={inputCls} data-testid="pf-category">{cols.map((c) => <option key={c.slug} value={c.slug}>{c.title}</option>)}</select></Field>
        <Field label="Price (IDR)"><NumInput value={f.price} onChange={n("price")} className={inputCls} data-testid="pf-price" /></Field>
        <Field label="Name (EN)" className="sm:col-span-2"><input value={f.name} onChange={s("name")} className={inputCls} data-testid="pf-name" /></Field>
        <Field label="URL slug"><input value={f.slug} onChange={s("slug")} placeholder="auto" className={inputCls} data-testid="pf-slug" /></Field>
        <Field label="Name (ID)" className="sm:col-span-3"><input value={f.name_id} onChange={s("name_id")} className={inputCls} data-testid="pf-name-id" /></Field>
        <Field label="Description (EN)" className="sm:col-span-3"><textarea rows={3} value={f.description} onChange={s("description")} className={inputCls} data-testid="pf-description" /></Field>
        <Field label="Description (ID)" className="sm:col-span-3"><textarea rows={3} value={f.description_id} onChange={s("description_id")} className={inputCls} /></Field>
      </div>
      <div className="space-y-1.5"><span className="text-xs text-[#6E6259]">Photos</span><ImageGallery images={f.images} onChange={(images) => setF({ ...f, images })} testid="pf-gallery" /></div>
      {f.type === "physical" && (
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Stock"><NumInput value={f.stock} onChange={n("stock")} className={inputCls} data-testid="pf-stock" /></Field>
          <Field label="Weight (grams)"><NumInput value={f.weight_g} onChange={n("weight_g")} className={inputCls} data-testid="pf-weight" /></Field>
          <Field label="Preparation before shipping (days)"><NumInput value={f.prep_days} onChange={n("prep_days")} className={inputCls} data-testid="pf-prep-days" /></Field>
          <Field label="When out of stock" className="sm:col-span-2">
            <select value={f.out_of_stock_mode} onChange={s("out_of_stock_mode")} className={inputCls} data-testid="pf-oos-mode">
              <option value="show">Keep showing, marked Sold out</option>
              <option value="hide">Hide from the store</option>
              <option value="preorder">Accept Pre-order (PO)</option>
            </select>
          </Field>
          {f.out_of_stock_mode === "preorder" && <Field label="PO duration (days)"><NumInput value={f.preorder_days} onChange={n("preorder_days")} className={inputCls} data-testid="pf-po-days" /></Field>}
          <div className="sm:col-span-3 space-y-1.5"><span className="text-xs text-[#6E6259]">Variants</span><VariantEditor variants={f.variants} onChange={(variants) => setF({ ...f, variants })} colors={colors} /></div>
        </div>
      )}
      {f.type === "digital" && (
        <div className="space-y-3">
          <Field label="Download limit per file"><NumInput value={f.download_limit} onChange={n("download_limit")} className={`${inputCls} w-32`} data-testid="pf-download-limit" /></Field>
          <p className="text-xs text-[#6E6259]">Variants (format) — default PDF; add DXF or others. The buyer picks one and downloads only that variant's file (files stay private).</p>
          <RowList testid="pf-files" rows={f.files} onChange={s("files")} add={() => ({ name: "", format: f.files.length ? "DXF" : "PDF", delta: 0 })}
            cols={[["format", "PDF / DXF / SVG", "text", "w-28"], ["name", "file name shown to buyer"], ["delta", "+ price (IDR)", "number", "w-32"], ["storage_path", "", "upload", "private"]]} />
        </div>
      )}
      {f.type === "course" && (
        <div className="space-y-2">
          <p className="text-xs text-[#6E6259]">Lessons — paste a YouTube / MP4 URL or upload a private video</p>
          <RowList testid="pf-lessons" rows={f.lessons} onChange={s("lessons")} add={() => ({ title: "", duration: "", video_url: "" })}
            cols={[["title", "Lesson title"], ["duration", "10 min", "text", "w-24"], ["video_url", "Video URL"], ["video_path", "", "upload", "private"]]} />
        </div>
      )}
      {f.type === "workshop" && (
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Format"><select value={f.mode || "inperson"} onChange={s("mode")} className={inputCls} data-testid="pf-mode"><option value="inperson">In-person</option><option value="zoom">Zoom</option></select></Field>
            <Field label="Venue (in-person)"><input value={f.venue} onChange={s("venue")} className={inputCls} data-testid="pf-venue" /></Field>
          </div>
          <p className="text-xs text-[#6E6259]">Sessions (booked seats are kept automatically)</p>
          <RowList testid="pf-sessions" rows={f.sessions} onChange={s("sessions")} add={() => ({ start: new Date().toISOString(), capacity: 8, meeting_link: "" })}
            cols={[["start", "", "datetime-local", "w-52"], ["capacity", "Seats", "number", "w-24"], ["meeting_link", "Zoom link (optional)"]]} />
        </div>
      )}
      {["course", "workshop"].includes(f.type) && (
        <Field label="Add-on kit upsell">
          <select value={f.addon_product_id || ""} onChange={(e) => setF({ ...f, addon_product_id: e.target.value || null })} className={inputCls} data-testid="pf-addon">
            <option value="">None</option>{all.filter((p) => p.type === "physical").map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <div className="flex gap-6">
        <label className="flex items-center gap-2"><Switch checked={f.featured} onCheckedChange={s("featured")} data-testid="pf-featured" />Featured on homepage</label>
        <label className="flex items-center gap-2"><Switch checked={f.active} onCheckedChange={s("active")} data-testid="pf-active" />Active</label>
      </div>
      <div className="flex justify-end gap-3 pt-2"><button onClick={onClose} className="px-4 text-sm">Cancel</button><Btn onClick={save} disabled={busy} data-testid="pf-save-btn">Save product</Btn></div>
    </div>
  );
}

function StockCell({ p, onSaved }) {
  const [v, setV] = useState(p.stock);
  if (p.type !== "physical") return <span className="text-[#6E6259]">—</span>;
  const save = async () => { try { await api.patch(`/admin/products/${p.product_id}/stock`, { stock: Number(v) }); toast.success("Stock updated"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex gap-1">
      <NumInput value={v} onChange={setV} className={`${inputCls} w-24 py-1`} data-testid={`stock-input-${p.slug}`} />
      {Number(v) !== p.stock && <button onClick={save} className="text-xs underline" data-testid={`stock-save-${p.slug}`}>Save</button>}
    </div>
  );
}

export default function Products() {
  const { user, fmt } = useApp();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [colorsOpen, setColorsOpen] = useState(false);
  const [colors, reloadColors] = useColors();
  const [q, setQ] = useState("");
  const isAdmin = user.role === "superadmin";
  const load = () => api.get("/admin/products").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}" permanently? This cannot be undone.`)) return;
    try { await api.delete(`/admin/products/${p.product_id}`); toast.success("Product deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const k = q.trim().toLowerCase();
  const filtered = rows.filter((p) => !k || [p.name, p.name_id, p.type, p.category, p.slug].some((x) => (x || "").toLowerCase().includes(k)));
  const { rows: list, sort, toggle } = useSort(filtered, PRODUCT_SORT);
  return (
    <div data-testid="admin-products">
      <AdminTitle action={isAdmin && <div className="flex gap-2"><button onClick={() => setColorsOpen(true)} className="border border-[#3B220C] px-4 text-sm" data-testid="color-library-btn">Color library</button><Btn onClick={() => setEdit(BLANK)} data-testid="new-product-btn" className="flex items-center gap-2"><Plus size={16} />New product</Btn></div>}>
        {isAdmin ? "Products & Inventory" : "Inventory"}
      </AdminTitle>
      <div className="relative mb-4 max-w-md"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E6259]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, type, collection..." className={`${inputCls} pl-9`} data-testid="admin-products-search" /></div>
      <div className="bg-white border border-[#E6DFD5] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#6E6259] border-b border-[#E6DFD5]"><tr>{PRODUCT_COLS.map(([h, key], i) => <SortTh key={i} label={h} k={key} sort={sort} toggle={toggle} testid="products" />)}</tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.product_id} className="border-b border-[#E6DFD5] last:border-0" data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-3"><img src={fileUrl(p.images?.[0])} alt="" className="w-10 h-10 object-cover" /></td>
                <td className="p-3">{p.name}</td>
                <td className="p-3 text-xs capitalize">{p.type}</td>
                <td className="p-3">{fmt(p.price)}</td>
                <td className="p-3"><StockCell key={p.stock} p={p} onSaved={load} /></td>
                <td className="p-3 text-xs">{p.active ? "Active" : "Archived"}</td>
                <td className="p-3">{isAdmin && <div className="flex gap-3"><button onClick={() => setEdit(p)} data-testid={`edit-product-${p.slug}`} aria-label="Edit"><Pencil size={14} /></button><button onClick={() => del(p)} data-testid={`delete-product-${p.slug}`} aria-label="Delete" className="text-[#9E2A2B]"><Trash2 size={14} /></button></div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={colorsOpen} onOpenChange={setColorsOpen}>
        <DialogContent className="max-w-xl rounded-none">
          <DialogTitle className="font-serif text-3xl font-normal">Color library</DialogTitle>
          <ColorLibrary colors={colors} reload={reloadColors} />
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogTitle className="font-serif text-3xl font-normal">{edit?.product_id ? "Edit product" : "New product"}</DialogTitle>
          {edit && <ProductForm initial={edit} all={rows} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
